import React, { useState } from 'react';

/**
 * SwipeResult - Post-swipe confirmation screen
 *
 * Shows:
 * - For right swipe (apply): Resume generated message, preview, copy/download
 * - For up swipe (defer): Brief "saved for later" message
 */

const SwipeResult = ({ result, generating, onContinue }) => {
  const [copied, setCopied] = useState(false);

  if (!result) return null;

  const { type, job, queueItem, resume, error } = result;

  // Deferred result - brief message
  if (type === 'deferred') {
    return (
      <div className="swipe-result swipe-result--deferred">
        <div className="swipe-result__content">
          <span className="swipe-result__icon">📌</span>
          <h3>Saved for later</h3>
          <p>{job?.company} - {job?.role || job?.title}</p>
        </div>
      </div>
    );
  }

  // Generating state
  if (type === 'applying' || generating) {
    return (
      <div className="swipe-result swipe-result--generating">
        <div className="swipe-result__content">
          <div className="swipe-result__spinner" />
          <h3>Generating your resume...</h3>
          <p>Tailoring for {job?.company}</p>
        </div>
      </div>
    );
  }

  // Applied result - show resume
  if (type === 'applied') {
    const company = job?.company || 'Unknown';
    const role = job?.role || job?.title || 'Role';
    const jobUrl = queueItem?.sourceUrl;

    // Copy resume to clipboard
    const handleCopy = async () => {
      if (!resume) return;

      const text = formatResumeAsText(resume);
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };

    // Download resume as text file
    const handleDownload = () => {
      if (!resume) return;

      const text = formatResumeAsText(resume);
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${resume.name || 'Resume'}_${company.replace(/[^a-z0-9]/gi, '')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    // Open job posting
    const handleOpenJob = () => {
      if (jobUrl) {
        window.open(jobUrl, '_blank');
      }
    };

    return (
      <div className="swipe-result swipe-result--applied">
        <div className="swipe-result__content">
          {/* Success header */}
          <div className="swipe-result__header">
            <span className="swipe-result__icon">🎉</span>
            <h3>Resume generated!</h3>
            <p>Tailored for {role} at {company}</p>
          </div>

          {/* Error state */}
          {error && (
            <div className="swipe-result__error">
              <p>{error}</p>
            </div>
          )}

          {/* Customizations */}
          {resume?.customizations && resume.customizations.length > 0 && (
            <div className="swipe-result__customizations">
              <h4>Key customizations:</h4>
              <ul>
                {resume.customizations.slice(0, 3).map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Resume preview */}
          {resume && (
            <div className="swipe-result__preview">
              <div className="swipe-result__preview-header">
                <strong>{resume.name}</strong>
              </div>
              {resume.summary && (
                <p className="swipe-result__preview-summary">{resume.summary}</p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="swipe-result__actions">
            {resume && (
              <>
                <button
                  onClick={handleCopy}
                  className="swipe-result__btn swipe-result__btn--copy"
                >
                  {copied ? '✓ Copied!' : '📋 Copy Resume'}
                </button>
                <button
                  onClick={handleDownload}
                  className="swipe-result__btn swipe-result__btn--download"
                >
                  📥 Download
                </button>
              </>
            )}
            {jobUrl && (
              <button
                onClick={handleOpenJob}
                className="swipe-result__btn swipe-result__btn--open"
              >
                🔗 Open Job Posting
              </button>
            )}
          </div>

          {/* Continue button */}
          <button
            onClick={onContinue}
            className="swipe-result__continue"
          >
            Continue Swiping →
          </button>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * Format resume object as plain text
 */
function formatResumeAsText(resume) {
  if (!resume) return '';

  const lines = [];

  // Name
  if (resume.name) {
    lines.push(resume.name.toUpperCase());
    lines.push('');
  }

  // Summary
  if (resume.summary) {
    lines.push('PROFESSIONAL SUMMARY');
    lines.push(resume.summary);
    lines.push('');
  }

  // Experience
  if (resume.experiences && resume.experiences.length > 0) {
    lines.push('EXPERIENCE');
    lines.push('');
    for (const exp of resume.experiences) {
      lines.push(`${exp.role} | ${exp.company}`);
      if (exp.period) lines.push(exp.period);
      if (exp.location) lines.push(exp.location);
      if (exp.bullets && exp.bullets.length > 0) {
        for (const bullet of exp.bullets) {
          lines.push(`• ${bullet}`);
        }
      }
      lines.push('');
    }
  }

  // Skills
  if (resume.skills && resume.skills.length > 0) {
    lines.push('SKILLS');
    lines.push(resume.skills.join(' • '));
    lines.push('');
  }

  // Education
  if (resume.education) {
    lines.push('EDUCATION');
    lines.push(resume.education);
    lines.push('');
  }

  // Activities
  if (resume.activities) {
    lines.push('ACTIVITIES & LEADERSHIP');
    lines.push(resume.activities);
  }

  return lines.join('\n');
}

export default SwipeResult;
